import React, { useState } from "react";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import StructuredData from "@/components/StructuredData";
import HeroSection from "@/components/HeroSection";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";

const Contact = () => {
  // ✅ Updated to match your old form fields
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    contact: "",
    organization: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ✅ Submit to sendmail.php using FormData (classic PHP-friendly POST)
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic front-end guard (required already on inputs)
    if (!formData.name || !formData.email || !formData.contact) {
      toast.error("Please fill in Name, Email, and Contact Number.");
      return;
    }

    setIsSubmitting(true);

    try {
      const fd = new FormData();
      fd.append("name", formData.name);
      fd.append("email", formData.email);
      fd.append("contact", formData.contact);
      fd.append("organization", formData.organization);
      fd.append("message", formData.message);
      fd.append("form_name", "contact-form");

      const res = await fetch(`${process.env.PUBLIC_URL}/sendmail.php`, {
        method: "POST",
        body: fd,
        headers: { Accept: "application/json" },
      });

      let payload = null;
      try {
        payload = await res.json();
      } catch {}

      if (!res.ok || !payload?.ok) {
        const msg = payload?.error || `Submit failed (${res.status})`;
        toast.error(msg);
        setIsSubmitting(false);
        return;
      }

      toast.success(
        payload.message || "Thank you! We'll get back to you soon."
      );

      // Reset form
      setFormData({
        name: "",
        email: "",
        contact: "",
        organization: "",
        message: "",
      });
    } catch (error) {
      console.error(error);
      toast.error(
        "Something went wrong while sending your message. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: "Email",
      details: "sales@gulfdigital-me.com",
      link: "mailto:sales@gulfdigital-me.com",
    },
    {
      icon: Clock,
      title: "Business Hours",
      details: "Sunday - Thursday: 9:00 AM - 5:00 PM",
      link: null,
    },
    {
      icon: MapPin,
      title: "Saudi Arabia Office",
      details: [
        "Dammam, Saudi Arabia",
        "Address: Al Hussaini Commercial Center, Prince Mohammed Bin Fahd Road, Al Tubayshi, Dammam 32233",
      ],
      link: null,
      phone: ["+966554002958"],
    },
    {
      icon: MapPin,
      title: "Bahrain Office",
      details: [
        "Arad, Bahrain",
        "Address: Street: PO. Box Bldg 03744, 4012 Rd, 0240 , City: Arad, State/Province/Area: Arad",
      ],
      link: null,
      phone: ["+97317672770", "+966554002958"],
    },
    {
      icon: MapPin,
      title: "Oman Office",
      details: [
        "Muscat, Oman",
        "Address: Plot No-71, Block No-03, Al Khuwayrah,Majees Road, Falaj Al Qabail PO Box 669, Postal Code 311 Sohar, Sultanate of Oman ",
      ],
      phone: ["+96894163999", "+96897561333"],
      link: null,
    },
  ];

  return (
    <>
      <SEO
        title="Contact Us - Get in Touch with Gulf Digital | Cybersecurity Experts"
        description="Contact Gulf Digital for cybersecurity solutions, managed services, and compliance advisory. Reach our team at +966554002958 or Sales@gulfdigital-me.com."
        url="/contact"
        ogTitle="Contact Gulf Digital | Cybersecurity Services in Saudi Arabia"
        ogDescription="Get in touch with Gulf Digital for Cybersecurity services in Saudi Arabia. Reach our team at +966554002958 or Sales@gulfdigital-me.com."
        twitterTitle="Contact Gulf Digital | Cybersecurity Solutions in Saudi Arabia"
        twitterDescription="Talk to Gulf Digital about Cybersecurity solutions in Saudi Arabia, including managed services, SOC, and compliance advisory."
      />
      <StructuredData type="LocalBusiness" data={{
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '+966554002958',
          contactType: 'Sales Support',
          email: 'sales@gulfdigital-me.com',
          hoursAvailable: 'Mo-Fr 09:00-18:00'
        }
      }} />

      <HeroSection
        eyebrow="Get in Touch"
        title="Let's Discuss Your Security Needs"
        subtitle="Our team of cybersecurity experts is ready to help you protect your organization. Reach out to us today."
        variant="minimal"
      />

      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Card className="border-2 border-warm-gray-200">
                <CardHeader>
                  <CardTitle className="text-2xl">Send us a Message</CardTitle>
                </CardHeader>
                <CardContent>
                  {/* NOTE: action/method kept for clarity; JS handles submit via fetch */}
                  <form
                    onSubmit={handleSubmit}
                    action="/sendmail.php"
                    method="post"
                    className="space-y-6"
                    data-testid="contact-form"
                    noValidate
                  >
                    {/* Name */}
                    <div>
                      <Label htmlFor="name">Your Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        data-testid="input-name"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <Label htmlFor="email">Your Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        data-testid="input-email"
                      />
                    </div>

                    {/* Contact Number */}
                    <div>
                      <Label htmlFor="contact">Your Contact Number *</Label>
                      <Input
                        id="contact"
                        name="contact"
                        type="tel"
                        value={formData.contact}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        data-testid="input-contact"
                      />
                    </div>

                    {/* Organization */}
                    <div>
                      <Label htmlFor="organization">Your Organization</Label>
                      <Input
                        id="organization"
                        name="organization"
                        value={formData.organization}
                        onChange={handleChange}
                        className="mt-1"
                        data-testid="input-organization"
                      />
                    </div>

                    {/* Message */}
                    <div>
                      <Label htmlFor="message">Your Message (optional)</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={5}
                        className="mt-1"
                        data-testid="input-message"
                      />
                    </div>

                    {/* Hidden field to mirror old form */}
                    <input
                      type="hidden"
                      name="form_name"
                      value="contact-form"
                    />

                    {/* Submit */}
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-brand-maroon hover:bg-brand-maroon-dark mt-2"
                      data-testid="submit-button"
                    >
                      {isSubmitting ? "Sending..." : "Submit"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-3xl font-bold mb-4">Contact Information</h2>
                <p className="text-lg text-muted-foreground">
                  Get in touch with our team of cybersecurity experts. We're
                  here to help you secure your digital infrastructure.
                </p>
              </div>

              <div className="space-y-6">
                {contactInfo.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="flex items-start space-x-4 p-4 rounded-xl hover:bg-warm-gray-50 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-lg bg-brand-maroon/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-brand-maroon" />
                    </div>

                    <div>
                      <h3 className="font-semibold mb-1">{item.title}</h3>

                      {item.link ? (
                        <a
                          href={item.link}
                          className="text-muted-foreground hover:text-brand-maroon transition-colors"
                        >
                          {item.details}
                        </a>
                      ) : Array.isArray(item.details) ? (
                        <div className="text-muted-foreground space-y-1">
                          {item.details.map((line, i) => (
                            <p key={i}>{line}</p>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground">{item.details}</p>
                      )}

                      {item.phone &&
                        Array.isArray(item.phone) &&
                        item.phone.length > 0 && (
                          <div className="text-muted-foreground space-y-1 mt-2">
                            <div className="flex items-center space-x-2">
                              <Phone className="w-4 h-4 text-brand-maroon" />
                              <span className="font-medium text-brand-maroon">
                                Phone:
                              </span>
                            </div>
                            <div className="pl-6 space-y-1">
                              {item.phone.map((num, i) => (
                                <a
                                  key={i}
                                  href={`tel:${num}`}
                                  className="block hover:text-brand-maroon transition-colors"
                                >
                                  {num}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
